import { createFileRoute } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { SettingsTabs } from "@/components/settings/settings-tabs";
import { DataTable, type Column } from "@/components/data/data-table";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { z } from "zod";
import { rolesApi, usersApi, type roleEntrySchema, type userEntrySchema } from "@/api/settings";

export const Route = createFileRoute("/_app/settings/access")({
  component: AccessSettings,
});

type User = z.infer<typeof userEntrySchema>;
type Role = z.infer<typeof roleEntrySchema>;

function AccessSettings() {
  return (
    <PageContainer>
      <PageHeader
        title="Users & Roles"
        description="Who can sign in to this tenant and what they are allowed to do."
      />
      <SettingsTabs
        tabs={[
          { value: "users", label: "Users", render: () => <UsersTab /> },
          { value: "roles", label: "Roles", render: () => <RolesTab /> },
        ]}
      />
    </PageContainer>
  );
}

function UsersTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = usersApi.useList();
  const m = usersApi.useMutations();

  const columns: Column<User>[] = [
    { id: "email", header: "Email", cell: (r) => r.email, sortValue: (r) => r.email },
    { id: "name", header: "Name", cell: (r) => r.displayName ?? "—" },
    { id: "provider", header: "Provider", cell: (r) => r.provider ?? "local", nowrap: true },
    {
      id: "mfa",
      header: "MFA",
      cell: (r) =>
        r.mfaEnabled ? (
          <Chip size="small" color="success" label="On" />
        ) : (
          <Chip size="small" label="Off" />
        ),
    },
    {
      id: "status",
      header: "Status",
      cell: (r) => <StatusChip status={r.status ?? "ACTIVE"} />,
    },
    { id: "last", header: "Last login", cell: (r) => <RelativeTime value={r.lastLoginAt} /> },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => {
        const disabled = (r.status ?? "").toUpperCase() === "DISABLED";
        return (
          <Button
            size="small"
            color={disabled ? "primary" : "error"}
            onClick={async (e) => {
              e.stopPropagation();
              const next = disabled ? "ACTIVE" : "DISABLED";
              if (
                await confirm({
                  title: `${disabled ? "Enable" : "Disable"} ${r.email}?`,
                  destructive: !disabled,
                })
              ) {
                await m.update.mutateAsync({ id: r.id, body: { status: next } });
                toast.success(disabled ? "User enabled" : "User disabled");
              }
            }}
          >
            {disabled ? "Enable" : "Disable"}
          </Button>
        );
      },
    },
  ];

  return (
    <Card>
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        error={error}
        onRetry={() => refetch()}
        rowKey={(r) => r.id}
        search={(r) => `${r.email} ${r.displayName ?? ""}`}
        searchPlaceholder="Search users…"
        empty={
          <EmptyState
            title="No users"
            description="Users appear here once they sign in or are invited."
          />
        }
      />
    </Card>
  );
}

function RolesTab() {
  const { data, isLoading, error, refetch } = rolesApi.useList();

  const columns: Column<Role>[] = [
    { id: "name", header: "Role", cell: (r) => r.name, sortValue: (r) => r.name },
    { id: "desc", header: "Description", cell: (r) => r.description ?? "—" },
    {
      id: "perms",
      header: "Permissions",
      cell: (r) => (r.permissions === "*" ? "All (admin)" : (r.permissions ?? "—")),
    },
    {
      id: "scope",
      header: "Scope",
      cell: (r) => (r.tenantId ? "Custom" : "Built-in"),
      nowrap: true,
    },
  ];

  return (
    <Card>
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        error={error}
        onRetry={() => refetch()}
        rowKey={(r) => r.id}
        empty={<EmptyState title="No roles" />}
      />
    </Card>
  );
}
